// Overlay/start screen, HUD (tally, market, sky chip, forecast), toasts,
// and the end-of-season screen.
// Extracted from legacy/tsuri.html — logic and constants are unchanged.
//
// By this step economy.ts, fishing.ts, player.ts, and building.ts all
// exist for real, so most of the HUD reads their state directly. What's
// still missing has no home in the 10-step plan (weather/day-night sim,
// rest/sleep, diving, the fishing net) — those values are taken as
// parameters instead (see EnvSnapshot, RestSnapshot) so this module stays
// complete now and just needs real data plugged in once those systems
// exist. The big `updateActions` decision tree from legacy (which button
// to show for jetty/boat/dive/net/sleep/etc.) is NOT reproduced here: it
// needs live state from nearly every system at once, so it belongs in
// main.ts (step 10). This module only provides the generic rendering
// primitive (renderActionBar) that main.ts's decision list feeds into.

import type * as THREE from 'three';
import type { RoomState } from '@net/protocol';
import { FIXED_MAP_LABEL, FIXED_MAP_SEED } from '@world/fixedMap';
import { FISH_TYPES, fishMatFor } from '@entities/fish';
import { player, basketSlots, getCamYaw } from '@entities/player';
import { remoteScores } from '@entities/remotePlayers';
import { AVATARS, avatarById, type AvatarDef } from '@entities/avatars';
import { getAvatarPortraits } from '@entities/avatarPortraits';
import {
  boat, cart, inBoat, spawnCart, addBoatUpgradeDecor, addCartUpgradeDecor, increaseBoatSpeed,
} from '@systems/building';
import {
  coins, wood, pearls, iceDaysLeft, saltLeft, PEARL_PRICE,
  CARRY_CAP, BOAT_CAP, CART_CAP, carryFish, boatFish, cartFish, type StoredFish,
  rodLevel, boatLevel, cartLevel, hasNet, jettyCount, speciesCounts, fishedAreas,
  BASE_PRICE, WOOD_BUNDLE, WOOD_SELL_PRICE, WOOD_BUY_PRICE, CART_PRICE,
  nearestVillage, priceFor, sellSpecies, sellWood, buyWood, sellPearls, buyCart, spendCoins,
  upgradeRod, upgradeBoat, upgradeCart, buyNet, buyIce, buySalt,
  rivals, contestants, AWARDS, type PlayerInfraFlags,
  setRivalCount,
} from '@economy/market';
import {
  lantern, giveLantern, refillLantern, LANTERN_PRICE, OIL_PRICE, OIL_FLASK, OIL_MAX,
} from '@systems/lantern';

function el<T extends HTMLElement = HTMLElement>(id: string): T {
  return document.getElementById(id) as T;
}

// Shared by the solo overlay's avatar row and the multiplayer lobby's
// avatar row: a button with a live-rendered portrait (see
// avatarPortraits.ts) above the avatar's name.
function buildAvatarButton(av: AvatarDef, portraits: Map<string, string>): HTMLButtonElement {
  const b = document.createElement('button');
  const img = document.createElement('img');
  img.src = portraits.get(av.id) ?? '';
  img.alt = av.name;
  b.appendChild(img);
  const label = document.createElement('span');
  label.textContent = av.name;
  b.appendChild(label);
  return b;
}

// ================= Toast =================
const toastEl = el('toast');
let toastTimer: ReturnType<typeof setTimeout> | null = null;

export function showToast(msg: string): void {
  toastEl.textContent = msg;
  toastEl.style.opacity = '1';
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toastEl.style.opacity = '0'; }, 1800);
}

// ================= Storage visuals =================
export function showFishIn(slots: THREE.Mesh[], arr: StoredFish[], per: number): void {
  slots.forEach((m, i) => {
    const has = arr.length > i * per;
    m.visible = has;
    if (has) m.material = fishMatFor(arr[Math.min(arr.length - 1, i * per)].n);
  });
}

export function updateStorageVisuals(): void {
  showFishIn(basketSlots, carryFish, 1);
  if (boat) showFishIn(boat.mesh.userData.fishSlots, boatFish, 2);
  if (cart) showFishIn(cart.mesh.userData.fishSlots, cartFish, 2);
}

// ================= Tally & standings =================
const tallyEl = el('tally');
const tallyRows = el('tallyRows');

// Lifetime breakdown behind "You"'s standings score — only available for
// the local player (remote players only sync their aggregate AWARDS
// counts, not the per-species/per-upgrade detail behind them).
let youExpanded = false;

function youBreakdownHTML(infra: PlayerInfraFlags): string {
  const species = [...speciesCounts.entries()].sort((a, b) => b[1] - a[1])
    .map(([name, n]) => name + ' ×' + n).join(', ');
  const infraBits: string[] = [];
  if (jettyCount) infraBits.push('Jetty ×' + jettyCount);
  if (infra.hasBoat) infraBits.push('Boat');
  if (infra.hasCart) infraBits.push('Cart');
  if (infra.hasShelter) infraBits.push('Shelter');
  const upgradeBits: string[] = [];
  if (rodLevel > 1) upgradeBits.push('Rod Lv' + rodLevel);
  if (boatLevel > 1) upgradeBits.push('Boat Lv' + boatLevel);
  if (cartLevel > 1) upgradeBits.push('Cart Lv' + cartLevel);
  if (hasNet) upgradeBits.push('Net');
  const rows = [
    '🐟 ' + (species || 'none yet'),
    '🗺️ ' + fishedAreas.size + ' water' + (fishedAreas.size === 1 ? '' : 's') + ' fished',
    '🔨 ' + (infraBits.join(', ') || 'none yet'),
    '⚙️ ' + (upgradeBits.join(', ') || 'none yet'),
    '🪵 ' + wood + ' wood on hand',
  ];
  return '<div style="opacity:0.8;font-size:0.9em;padding-left:1em">' + rows.join('<br>') + '</div>';
}

function standingsHTML(infra: PlayerInfraFlags): string {
  const remote = remoteScores();
  if (!rivals.length && !remote.length) return '';
  return '<br><b>🏆 Standings</b><br>' + contestants(infra, remote).map((c, i) => {
    const row = (i + 1) + '. ' + (c.you
      ? '<span class="you-toggle" style="cursor:pointer">' + (youExpanded ? '▾' : '▸') + ' <b>You</b></span>'
      : c.name) + ' — ' + c.pts + ' pts ' + c.trophies;
    return c.you && youExpanded ? row + '<br>' + youBreakdownHTML(infra) : row;
  }).join('<br>');
}

export function showTally(): void {
  tallyEl.style.display = 'block';
}

// Tapping/clicking "The Catch" header collapses the rows below it — on
// phones the tally can grow tall enough (species breakdown + standings)
// to run off the bottom of the screen, so it needs a way to be tucked
// away rather than only ever capped/scrolling.
export function initTallyToggle(): void {
  tallyEl.querySelector('h3')?.addEventListener('click', () => {
    tallyEl.classList.toggle('collapsed');
  });
  // Event delegation, not a direct listener: tallyRows' contents (including
  // the "You" row) are fully rebuilt by innerHTML on every renderTally()
  // call, so anything attached directly to that row would be lost the next
  // render. tallyRows itself is never replaced, so delegation here holds.
  tallyRows.addEventListener('click', (e) => {
    if (!(e.target as HTMLElement).closest('.you-toggle')) return;
    youExpanded = !youExpanded;
    renderTally(lastRestText, lastInfra);
  });
}

let lastRestText = '';
let lastInfra: PlayerInfraFlags = { hasBoat: false, hasCart: false, hasShelter: false };

// restText is pre-formatted (e.g. "💤 Rest 82 ✨ well rested") since the
// rest/sleep system isn't extracted yet — see the module header.
export function renderTally(restText: string, infra: PlayerInfraFlags): void {
  lastRestText = restText;
  lastInfra = infra;
  const lines = [
    '🪙 Coins × ' + coins,
    '🪵 Wood × ' + wood,
    '🧺 Basket: ' + carryFish.length + '/' + CARRY_CAP,
  ];
  if (boat) lines.push('🛶 Boat hold: ' + boatFish.length + '/' + BOAT_CAP);
  if (cart) lines.push('🛒 Cart: ' + cartFish.length + '/' + CART_CAP);
  if (pearls) lines.push('🦪 Pearls × ' + pearls);
  if (iceDaysLeft > 0) lines.push('🧊 Ice: ' + iceDaysLeft + ' day' + (iceDaysLeft > 1 ? 's' : ''));
  if (saltLeft > 0) lines.push('🧂 Salt: ' + saltLeft);
  lines.push(restText);
  const totals: Record<string, { c: number; aging: number; salted: number }> = {};
  carryFish.concat(boatFish, cartFish).forEach((fish) => {
    const t = totals[fish.n] || (totals[fish.n] = { c: 0, aging: 0, salted: 0 });
    t.c++;
    if (fish.salted) t.salted++;
    else if (fish.f < 2.5) t.aging++;
  });
  const sp = Object.keys(totals).map((n) => {
    const t = totals[n];
    const marks: string[] = [];
    if (t.aging) marks.push('⏳' + t.aging);
    if (t.salted) marks.push('🧂' + t.salted);
    return n + ' × ' + t.c + (marks.length ? ' <span style="opacity:0.8">(' + marks.join(' ') + ')</span>' : '');
  }).join('<br>');
  tallyRows.innerHTML = lines.join('<br>')
    + (sp ? '<br><span style="opacity:0.7">' + sp + '</span>' : '')
    + standingsHTML(infra);
}

// ================= Village market panel =================
const marketEl = el('market');
const marketRows = el('marketRows');
const marketDirEl = el('marketDir');
const marketDirText = el('marketDirText');
let marketSig = '';

export function updateMarketUI(now: number): void {
  const near = nearestVillage(player.x, player.z, now);
  const open = !!(near && near.d < 3.0 && !inBoat);
  marketEl.style.display = open ? 'block' : 'none';
  // Direction hint toward the nearest market when carrying fish
  if (near && !open && (carryFish.length + cartFish.length) > 0 && near.d > 6) {
    marketDirEl.style.display = 'block';
    const bearing = Math.atan2(near.v.z - player.z, near.v.x - player.x);
    const camYaw = getCamYaw();
    const fwdA = Math.atan2(-Math.cos(camYaw), -Math.sin(camYaw));
    (marketDirEl.querySelector('.arrow') as HTMLElement).style.transform = 'rotate(' + (bearing - fwdA) + 'rad)';
    marketDirText.textContent = 'Market ' + Math.round(near.d) + 'm';
  } else {
    marketDirEl.style.display = 'none';
  }
  if (!open) { marketSig = ''; return; }
  const v = near!.v;
  const cartHere = !!(cart && Math.hypot(cart.x - v.x, cart.z - v.z) < 3.2);
  const counts: Record<string, number> = {}, aging: Record<string, number> = {};
  function tally2(fish: StoredFish): void {
    counts[fish.n] = (counts[fish.n] || 0) + 1;
    if (fish.f < 2.5) aging[fish.n] = (aging[fish.n] || 0) + 1;
  }
  carryFish.forEach(tally2);
  if (cartHere) cartFish.forEach(tally2);
  const sig = JSON.stringify([
    FISH_TYPES.map((t) => priceFor(v, t.name, now)), counts, aging, coins, wood, pearls,
    iceDaysLeft, saltLeft, !!cart, rodLevel, boatLevel, cartLevel, hasNet,
  ]);
  if (sig === marketSig) return;
  marketSig = sig;
  marketRows.innerHTML = '';

  FISH_TYPES.forEach((t) => {
    const price = priceFor(v, t.name, now);
    const have = counts[t.name] || 0;
    const row = document.createElement('div');
    row.className = 'row' + (have ? '' : ' muted');
    const trend = price > BASE_PRICE[t.name] ? ' ▲' : (price < BASE_PRICE[t.name] ? ' ▼' : '');
    const ageMark = aging[t.name] ? ' ⏳' : '';
    row.innerHTML = '<span>' + t.name + ' ' + price + '🪙' + trend + '</span><span>× ' + have + ageMark + '</span>';
    if (have) {
      const b = document.createElement('button');
      b.textContent = 'Sell';
      b.addEventListener('click', () => {
        const r = sellSpecies(v, t.name, cartHere, performance.now());
        if (r.sold) {
          updateStorageVisuals();
          showToast('💰 Sold ' + r.sold + ' ' + t.name + ' for ' + r.earned + ' coins.');
        }
      });
      row.appendChild(b);
    }
    marketRows.appendChild(row);
  });

  const bw = document.createElement('button');
  bw.className = 'buy';
  bw.textContent = 'Buy ' + WOOD_BUNDLE + ' Wood — ' + WOOD_BUY_PRICE + '🪙';
  bw.addEventListener('click', () => {
    const r = buyWood();
    showToast(r.success ? '🪵 Bought ' + WOOD_BUNDLE + ' wood.' : 'Not enough coins.');
  });
  marketRows.appendChild(bw);

  if (wood >= WOOD_BUNDLE) {
    const sw = document.createElement('button');
    sw.className = 'buy';
    sw.textContent = 'Sell ' + WOOD_BUNDLE + ' Wood — ' + WOOD_SELL_PRICE + '🪙 (have ' + wood + ')';
    sw.addEventListener('click', () => {
      const r = sellWood();
      showToast(r.success ? '🪵 Sold ' + WOOD_BUNDLE + ' wood for ' + WOOD_SELL_PRICE + ' coins.' : 'Not enough wood to sell (need ' + WOOD_BUNDLE + ').');
    });
    marketRows.appendChild(sw);
  }
  if (pearls > 0) {
    const bp = document.createElement('button');
    bp.className = 'buy';
    bp.textContent = 'Sell ' + pearls + ' Pearl' + (pearls > 1 ? 's' : '') + ' — ' + (pearls * PEARL_PRICE) + '🪙';
    bp.addEventListener('click', () => {
      const r = sellPearls();
      if (r.sold) showToast('🦪 Sold ' + r.sold + ' pearl' + (r.sold > 1 ? 's' : '') + ' for ' + r.earned + ' coins.');
    });
    marketRows.appendChild(bp);
  }
  function buyBtn(label: string, fn: () => void): void {
    const b = document.createElement('button');
    b.className = 'buy';
    b.textContent = label;
    b.addEventListener('click', fn);
    marketRows.appendChild(b);
  }
  if (!lantern.owned) {
    buyBtn('Buy Lantern — ' + LANTERN_PRICE + '🪙 (see at night)', () => {
      if (!spendCoins(LANTERN_PRICE)) { showToast('Not enough coins for a lantern.'); return; }
      giveLantern(OIL_FLASK);
      showToast('🪔 Fishing lantern bought — it holds a flask of oil.');
    });
  } else if (lantern.oil < OIL_MAX) {
    buyBtn('Buy Fish Oil — ' + OIL_PRICE + '🪙 (' + OIL_FLASK + ' units)', () => {
      if (!spendCoins(OIL_PRICE)) { showToast('Not enough coins for oil.'); return; }
      const added = refillLantern(OIL_FLASK);
      showToast('🪔 Filled — ' + Math.round(added) + ' units of oil.');
    });
  }
  if (!cart) {
    buyBtn('Buy Cart — ' + CART_PRICE + '🪙 (holds 20 fish)', () => {
      const r = buyCart();
      if (r.success) {
        spawnCart(v.x + 0.9, v.z + 0.6, player.heading);
        showToast('🛒 Cart purchased! Grab it to pull it along.');
      } else {
        showToast('Not enough coins for a cart.');
      }
    });
  } else if (cartLevel < 2) {
    buyBtn('Upgrade Cart — 50🪙 (+15 capacity)', () => {
      const r = upgradeCart();
      if (r.success) {
        addCartUpgradeDecor();
        showToast('🛒 Cart upgraded — now hauls ' + CART_CAP + ' fish!');
      } else {
        showToast('Not enough coins.');
      }
    });
  }
  buyBtn('Buy Himuro Ice — 10🪙 (slows spoilage, 3 days)', () => {
    const r = buyIce();
    if (r.success) showToast('🧊 Himuro ice packed in — the catch will keep.');
    else showToast(r.reason === 'chest-full' ? 'The ice chest is already packed full.' : 'Not enough coins.');
  });
  buyBtn('Buy Salt — 6🪙 (preserves 5 fish at 60% value)', () => {
    const r = buySalt();
    if (r.success) showToast('🧂 Salt bought — enough for ' + saltLeft + ' fish.');
    else showToast(r.reason === 'salt-full' ? 'He has all the salt he can carry.' : 'Not enough coins.');
  });
  if (!hasNet) {
    buyBtn('Buy Net — 25🪙 (haul schools from the boat)', () => {
      const r = buyNet();
      showToast(r.success ? '🕸️ Net purchased — cast it from the boat over a school!' : 'Not enough coins.');
    });
  }
  if (rodLevel < 3) {
    const price = rodLevel === 1 ? 30 : 70;
    buyBtn('Upgrade Rod ' + (rodLevel === 1 ? 'II' : 'III') + ' — ' + price + '🪙 (longer casts)', () => {
      const r = upgradeRod(price);
      showToast(r.success ? '🎣 Rod upgraded to level ' + r.newLevel + ' — longer casts!' : 'Not enough coins.');
    });
  }
  if (boat && boatLevel < 2) {
    buyBtn('Upgrade Boat — 60🪙 (faster, +12 hold)', () => {
      const r = upgradeBoat();
      if (r.success) {
        increaseBoatSpeed(r.speedBonus);
        addBoatUpgradeDecor();
        showToast('🛶 Boat upgraded — faster, and holds ' + BOAT_CAP + ' fish!');
      } else {
        showToast('Not enough coins.');
      }
    });
  }
}

// ================= Sky chip & 7-day forecast =================
// Weather simulation (ENV, weatherForDay, gameTime, ...) isn't extracted
// (no home in the 10-step plan — see market.ts's header note), so the
// weather-for-a-given-day lookup is taken as a parameter here.
export interface EnvSnapshot {
  weather: string;
  day: number;
  frac: number;
  night: boolean;
  fullMoon: boolean;
  fastNight: boolean;
}

const WEATHER_ICON: Record<string, string> = { Clear: '☀️', Cloudy: '⛅', Rain: '🌧️', Storm: '⛈️' };
const MOON_ICONS = ['🌑', '🌒', '🌓', '🌔', '🌕', '🌖', '🌗', '🌘'];

function partOfDay(frac: number): string {
  if (frac < 0.21 || frac >= 0.81) return 'Night';
  if (frac < 0.3) return 'Dawn';
  if (frac < 0.72) return 'Day';
  return 'Dusk';
}
function moonPhase(day: number): number {
  return ((day % 8) + 8) % 8;
}
function isFullMoon(day: number): boolean {
  return moonPhase(day) === 4;
}

const skychipEl = el('skychip');
const forecastEl = el('forecast');
const forecastRows = el('forecastRows');
let forecastOpen = false;
let forecastDay = -1;

export function initSkyChip(): void {
  skychipEl.addEventListener('click', () => {
    forecastOpen = !forecastOpen;
    forecastEl.style.display = forecastOpen ? 'block' : 'none';
  });
}

export function showSkyChip(): void {
  skychipEl.style.display = 'block';
}

export function setForecastTitle(worldLabel: string): void {
  (document.querySelector('#forecast h3') as HTMLElement).textContent = '📅 Forecast — ' + worldLabel;
}

function renderForecast(startDay: number, weatherForDay: (day: number) => string): void {
  forecastDay = startDay;
  forecastRows.innerHTML = '';
  for (let i = 0; i < 7; i++) {
    const d = startDay + i;
    const w = weatherForDay(d);
    const row = document.createElement('div');
    row.className = 'frow' + (w === 'Storm' ? ' storm' : '');
    const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : 'Day ' + (d + 1);
    row.innerHTML = '<span>' + label + '</span><span>' + WEATHER_ICON[w] + ' ' + w + ' ' + MOON_ICONS[moonPhase(d)] + '</span>';
    forecastRows.appendChild(row);
    if (isFullMoon(d)) {
      const note = document.createElement('div');
      note.className = 'frow full';
      note.innerHTML = '<span></span><span>🌕 Full moon — night fishing!</span>';
      forecastRows.appendChild(note);
    }
  }
}

export function updateSkyChip(env: EnvSnapshot, seasonDays: number, weatherForDay: (day: number) => string): void {
  let txt = WEATHER_ICON[env.weather] + ' Day ' + (env.day + 1) + '/' + seasonDays + ' · ' + partOfDay(env.frac);
  if (env.night) txt += ' ' + MOON_ICONS[moonPhase(env.day)];
  if (env.fastNight) txt += ' ⏩';
  skychipEl.textContent = txt + ' ▾';
  if (forecastOpen && forecastDay !== env.day) renderForecast(env.day, weatherForDay);
}

// ================= End of season screen =================
export function initEndScreen(onSailAgain: () => void): void {
  el('againBtn').addEventListener('click', onSailAgain);
}

export function showEndScreen(worldLabel: string, infra: PlayerInfraFlags): void {
  el('actions').innerHTML = '';
  marketEl.style.display = 'none';
  marketDirEl.style.display = 'none';
  forecastEl.style.display = 'none';
  const rows = contestants(infra, remoteScores());
  const winnerYou = rows.length === 1 || rows[0].you;
  el('endTitle').textContent = winnerYou ? '🏮 A Prosperous Season!' : 'The Season Ends...';
  el('endSub').textContent = winnerYou
    ? (rivals.length ? 'The old fisherman retires the finest on the water.' : 'A season well fished on ' + worldLabel + '.')
    : 'Outdone this year — but the fish will bite again.';
  let html = rows.map((r, i) => {
    const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : '·';
    return '<div class="erow' + (r.you ? ' you' : '') + '">' + medal + ' ' + r.name + ' — ' + r.pts + ' pts ' + r.trophies + '</div>';
  }).join('');
  html += '<div style="margin-top:14px;font-size:14px;opacity:0.85">'
    + AWARDS.filter((a) => a.holder).map((a) =>
        a.icon + ' ' + a.name + ' (+' + a.pts + ', ' + a.desc + '): ' + a.holder
      ).join('<br>')
    + '</div>';
  el('endRows').innerHTML = html;
  el('endScreen').style.display = 'flex';
}

// ================= Overlay / start screen =================
export interface Island {
  name: string;
  seed: number | null;
  blurb: string;
  fixedMap?: boolean; // world/fixedMap.ts's hand-authored board instead of procedural generation
}

export const ISLANDS: Island[] = [
  { name: 'Heron Lake', seed: 2136, blurb: 'One great calm lake with a village at its shore — a gentle start.' },
  { name: 'Thousand Arms', seed: 4134, blurb: 'A maze of winding channels and reedy banks — the boat rules here.' },
  { name: 'Mangrove Coast', seed: 2529, blurb: 'Shallow, mangrove-choked waters teeming with shore fish.' },
  { name: 'The Deep Tarn', seed: 2549, blurb: 'Dark, deep waters hide wrecks, pearls and the biggest fish.' },
  { name: 'Far Villages', seed: 2962, blurb: 'Rich fishing grounds, but a long haul to any market.' },
  { name: 'Uncharted Waters', seed: null, blurb: 'A random island every voyage.' },
  { name: FIXED_MAP_LABEL, seed: FIXED_MAP_SEED, blurb: 'A fixed island, the same every time — two markets, three wrecks, every water type.', fixedMap: true },
];

function seedFromString(str: string): number {
  const asNum = parseInt(str, 10);
  if (!isNaN(asNum) && String(asNum) === str.trim()) return Math.abs(asNum) % 1000000;
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % 1000000;
}

export interface StartChoice {
  seed: number;
  worldLabel: string;
  avatarId: string;
  useFixedMap: boolean;
}

const IS_TOUCH = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;

// Wires the island/rival/seed picker and the Start button. onStart is
// called with the chosen seed/label; the caller (main.ts) is responsible
// for initWorld/setupRivals and any other game-state it owns.
export function initOverlay(onStart: (choice: StartChoice) => void): void {
  let selectedIsland = 0;
  let selectedAvatarId = AVATARS[0].id;

  const avatarRow = el('avatarRow');
  const avatarBlurb = el('avatarBlurb');
  const avatarPortraits = getAvatarPortraits();
  AVATARS.forEach((av, i) => {
    const b = buildAvatarButton(av, avatarPortraits);
    b.addEventListener('click', () => {
      selectedAvatarId = av.id;
      avatarBlurb.textContent = av.blurb;
      Array.from(avatarRow.children).forEach((c, j) => c.classList.toggle('sel', j === i));
    });
    if (i === 0) b.classList.add('sel');
    avatarRow.appendChild(b);
  });
  avatarBlurb.textContent = AVATARS[0].blurb;

  const islandRow = el('islandRow');
  const islandBlurb = el('islandBlurb');
  const seedInput = el<HTMLInputElement>('seedInput');
  ISLANDS.forEach((isl, i) => {
    const b = document.createElement('button');
    b.textContent = isl.name;
    b.addEventListener('click', () => {
      selectedIsland = i;
      seedInput.value = '';
      islandBlurb.textContent = isl.blurb;
      Array.from(islandRow.children).forEach((c, j) => c.classList.toggle('sel', j === i));
    });
    if (i === 0) b.classList.add('sel');
    islandRow.appendChild(b);
  });
  islandBlurb.textContent = ISLANDS[0].blurb;

  const rivalRow = el('rivalRow');
  [0, 1, 2, 3].forEach((n) => {
    const b = document.createElement('button');
    b.textContent = n === 0 ? 'None' : String(n);
    if (n === 2) b.classList.add('sel');
    b.addEventListener('click', () => {
      setRivalCount(n);
      Array.from(rivalRow.querySelectorAll('button')).forEach((c, j) => c.classList.toggle('sel', j === n));
    });
    rivalRow.appendChild(b);
  });

  el('startBtn').addEventListener('click', () => {
    let chosenSeed: number, worldLabel: string, useFixedMap = false;
    const custom = seedInput.value.trim();
    if (custom) {
      chosenSeed = seedFromString(custom);
      worldLabel = 'Seed ' + chosenSeed;
    } else {
      const isl = ISLANDS[selectedIsland];
      chosenSeed = isl.seed != null ? isl.seed : (1 + Math.floor(Math.random() * 999999));
      worldLabel = isl.name;
      useFixedMap = !!isl.fixedMap;
    }
    onStart({ seed: chosenSeed, worldLabel, avatarId: selectedAvatarId, useFixedMap });
    revealGameUI(worldLabel, chosenSeed);
  });
}

// Shared by solo start and the multiplayer game_start handler in main.ts:
// hides the overlay and reveals the in-game HUD once a world is chosen.
export function revealGameUI(worldLabel: string, seed: number): void {
  setForecastTitle(worldLabel);
  el('overlay').style.display = 'none';
  el('hint').style.display = 'block';
  showTally();
  el('brand').style.display = 'block';
  if (IS_TOUCH) el('joystick').style.display = 'block';
  showSkyChip();
  showToast('🗺️ ' + worldLabel + ' — seed ' + seed);
  setTimeout(() => { el('hint').style.display = 'none'; }, 4500);
}

// ================= Contextual action bar =================
export interface ActionItem {
  id: string;
  label: string;
  fn: () => void;
}

const actionsEl = el('actions');
let actionsSig = '';

// Generic renderer: main.ts computes the list of currently-available
// actions (build jetty, board boat, sleep, repair, ...) since that
// decision needs live state from nearly every system at once. This just
// diffs against the last rendered signature and rebuilds the buttons.
export function renderActionBar(list: ActionItem[]): void {
  const sig = list.map((l) => l.id).join(',');
  if (sig === actionsSig) return;
  actionsSig = sig;
  actionsEl.innerHTML = '';
  list.forEach((l) => {
    const b = document.createElement('button');
    b.textContent = l.label;
    b.addEventListener('click', l.fn);
    actionsEl.appendChild(b);
  });
}

// ================= Multiplayer lobby =================
export interface LobbyHooks {
  onCreateRoom: (name: string) => void;
  onJoinRoom: (code: string, name: string) => void;
  onSetReady: (ready: boolean) => void;
  onSetAvatar: (avatarId: string) => void;
  onSetFixedMap: (useFixedMap: boolean) => void;
  onStartGame: () => void;
  onLeaveRoom: () => void;
}

const lobbyPanelEl = el('lobbyPanel');
const lobbyJoinView = el('lobbyJoinView');
const lobbyRoomView = el('lobbyRoomView');
const lobbyNameInput = el<HTMLInputElement>('lobbyNameInput');
const joinCodeInput = el<HTMLInputElement>('joinCodeInput');
const roomCodeDisplay = el('roomCodeDisplay');
const lobbyPlayersEl = el('lobbyPlayers');
const lobbyAvatarRowEl = el('lobbyAvatarRow');
const lobbyAvatarBlurbEl = el('lobbyAvatarBlurb');
const readyBtn = el<HTMLButtonElement>('readyBtn');
const lobbyStartBtn = el<HTMLButtonElement>('lobbyStartBtn');
const mapToggleBtn = el<HTMLButtonElement>('mapToggleBtn');

let lobbyPlayersSig = '';
let lobbyAvatarButtons: HTMLButtonElement[] = [];
let currentUsesFixedMap = false;

function playerName(): string {
  return lobbyNameInput.value.trim() || 'Fisherman';
}

// Wires the Solo/"Play with Friends" toggle and the create/join/ready/start/
// leave controls. Rendering the room's live state is renderLobby's job —
// this only wires the one-shot button clicks main.ts reacts to.
export function initLobby(hooks: LobbyHooks): void {
  const soloModeBtn = el('soloModeBtn');
  const mpModeBtn = el('mpModeBtn');
  const soloRow = el('soloRow');
  mpModeBtn.addEventListener('click', () => {
    mpModeBtn.classList.add('sel');
    soloModeBtn.classList.remove('sel');
    soloRow.style.display = 'none';
    lobbyPanelEl.style.display = 'block';
  });
  soloModeBtn.addEventListener('click', () => {
    soloModeBtn.classList.add('sel');
    mpModeBtn.classList.remove('sel');
    soloRow.style.display = '';
    lobbyPanelEl.style.display = 'none';
  });

  const lobbyAvatarPortraits = getAvatarPortraits();
  lobbyAvatarButtons = AVATARS.map((av) => {
    const b = buildAvatarButton(av, lobbyAvatarPortraits);
    b.addEventListener('click', () => hooks.onSetAvatar(av.id));
    lobbyAvatarRowEl.appendChild(b);
    return b;
  });

  el('createRoomBtn').addEventListener('click', () => hooks.onCreateRoom(playerName()));
  el('joinRoomBtn').addEventListener('click', () => {
    const code = joinCodeInput.value.trim();
    if (code) hooks.onJoinRoom(code, playerName());
  });
  readyBtn.addEventListener('click', () => {
    const nowReady = readyBtn.textContent !== '✅ Ready';
    hooks.onSetReady(nowReady);
  });
  lobbyStartBtn.addEventListener('click', () => hooks.onStartGame());
  mapToggleBtn.addEventListener('click', () => hooks.onSetFixedMap(!currentUsesFixedMap));
  el('leaveRoomBtn').addEventListener('click', () => {
    hooks.onLeaveRoom();
    lobbyRoomView.style.display = 'none';
    lobbyJoinView.style.display = 'block';
    lobbyPlayersSig = '';
  });
}

export function renderLobby(room: RoomState, myPlayerId: string): void {
  lobbyJoinView.style.display = 'none';
  lobbyRoomView.style.display = 'block';
  roomCodeDisplay.textContent = room.code;

  const me = room.players.find((p) => p.id === myPlayerId);
  readyBtn.textContent = me?.ready ? '✅ Ready' : 'Ready Up';
  const isHost = !!me?.isHost;
  lobbyStartBtn.style.display = isHost ? 'inline-block' : 'none';
  lobbyStartBtn.disabled = !(room.players.length > 0 && room.players.every((p) => p.ready));

  currentUsesFixedMap = room.settings.useFixedMap;
  mapToggleBtn.disabled = !isHost;
  mapToggleBtn.textContent = currentUsesFixedMap ? '🗺️ ' + FIXED_MAP_LABEL : '🌊 Procedural world';
  mapToggleBtn.title = isHost ? 'Click to switch map' : 'Only the host can change the map';

  const takenByOthers = new Set(room.players.filter((p) => p.id !== myPlayerId).map((p) => p.avatarId));
  AVATARS.forEach((av, i) => {
    const b = lobbyAvatarButtons[i];
    b.classList.toggle('sel', me?.avatarId === av.id);
    b.disabled = takenByOthers.has(av.id);
  });
  lobbyAvatarBlurbEl.textContent = avatarById(me?.avatarId).blurb;

  const sig = JSON.stringify(room.players);
  if (sig === lobbyPlayersSig) return;
  lobbyPlayersSig = sig;
  lobbyPlayersEl.innerHTML = room.players.map((p) =>
    '<div>' + (p.ready ? '✅' : '⬜') + ' ' + p.name + ' — ' + avatarById(p.avatarId).name
    + (p.isHost ? ' 👑' : '') + (p.id === myPlayerId ? ' (you)' : '') + '</div>'
  ).join('');
}

// ================= Fullscreen toggle =================
// iPhone Safari doesn't support JS-triggered fullscreen for a regular
// page at all (Apple restricts the Fullscreen API to <video> there), so
// the button stays hidden rather than sitting dead on screen. Desktop
// browsers, Android Chrome, and iPadOS Safari all support the standard
// API; older Safari (desktop/iPad) needs the webkit-prefixed fallback.
interface WebkitFullscreenDoc extends Document {
  webkitFullscreenElement?: Element;
  webkitExitFullscreen?: () => void;
}
interface WebkitFullscreenEl extends HTMLElement {
  webkitRequestFullscreen?: () => void;
}

export function initFullscreenButton(): void {
  const btn = el<HTMLButtonElement>('fullscreenBtn');
  const root = document.documentElement as WebkitFullscreenEl;
  const doc = document as WebkitFullscreenDoc;
  if (typeof root.requestFullscreen !== 'function' && typeof root.webkitRequestFullscreen !== 'function') return;
  btn.style.display = 'flex';

  function isFullscreen(): boolean {
    return !!(document.fullscreenElement || doc.webkitFullscreenElement);
  }
  function updateIcon(): void {
    btn.textContent = isFullscreen() ? '⤦' : '⛶';
  }
  btn.addEventListener('click', () => {
    if (isFullscreen()) {
      if (document.exitFullscreen) document.exitFullscreen();
      else doc.webkitExitFullscreen?.();
    } else if (root.requestFullscreen) {
      root.requestFullscreen();
    } else {
      root.webkitRequestFullscreen?.();
    }
  });
  document.addEventListener('fullscreenchange', updateIcon);
  document.addEventListener('webkitfullscreenchange', updateIcon);
}
